import { Injectable, signal, computed, inject, effect, EffectRef, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { JsonaService } from './caps.service';
import { AuthService, UserInfo } from './auth.service';
import { ImpersonationContextService, ImpersonationTarget } from './impersonation-context.service';
import { AppErrorService } from './app-error.service';
import { UserRole, mapApiRoleToUserRole, ROLE_TIERS, GROUP_DISPLAY_NAMES } from '../user-management/role-config';
import { ENV } from '../../environments/environment';

export interface UserPermission {
  readonly key: string;
  readonly label: string;
  readonly groups: readonly string[];
  readonly isAcademicOfficer: boolean;
  readonly isCuimc: boolean;
  /** Privilege/scope tier (1 = widest … 4 = narrowest), used to disable
   *  impersonation targets that exceed the current user's own scope. */
  readonly tier: number;
  /** When set, this target requires the user to pick a reporting unit or
   *  department before it can be impersonated. */
  readonly scopeType?: 'reporting_unit' | 'department';
  /**
   * Groups whose members may impersonate this target. Omitted means the entry
   * is not an impersonation target at all — it can still be selected by someone
   * who actually holds that role (switching which of your own roles you act as).
   */
  readonly impersonatableBy?: readonly string[];
}

/** Academic Appointments roles — the widest impersonation authority. */
const AA_GROUPS = [
  'academic_appoint_admin',
  'academic_appoint_user',
  'academic_appoint_user_cuimc',
] as const;

const RU_ADMIN_GROUP   = 'school_institute_libraries_level';
const DEPT_ADMIN_GROUP = 'department_center_level';

/**
 * Who may open the impersonation picker at all. Everyone else (including the
 * Vice Provost for Faculty Affairs and plain academic officers) cannot view as
 * anyone.
 */
export const IMPERSONATION_CAPABLE_GROUPS: readonly string[] = [
  ...AA_GROUPS, RU_ADMIN_GROUP, DEPT_ADMIN_GROUP,
];

export interface CategorizedRoles {
  readonly tier1: UserRole[];
  readonly tier2: UserRole[];
  readonly tier3: UserRole[];
}

/**
 * Orders roles widest-tier-first. Within the same tier, academic_appoint_admin
 * sorts before academic_appoint_user so a user holding both roles resolves to
 * the admin permission rather than an array-order-dependent tie.
 */
function compareRolesWidestFirst(a: UserRole, b: UserRole): number {
  const tierDiff = (ROLE_TIERS[a.group_name] ?? 99) - (ROLE_TIERS[b.group_name] ?? 99);
  if (tierDiff !== 0) return tierDiff;
  if (a.group_name === 'academic_appoint_admin') return -1;
  if (b.group_name === 'academic_appoint_admin') return 1;
  return 0;
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly capsService = inject(JsonaService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly appErrorService = inject(AppErrorService);
  private readonly impersonationContext = inject(ImpersonationContextService);
  private readonly injector = inject(Injector);

  /** How long anyone waits on GET /me/ before proceeding without it. */
  static readonly ROLE_RESOLUTION_TIMEOUT_MS = 10_000;

  // Signals for reactive state
  readonly isSignedIn = signal<boolean>(false);
  readonly currentUser = signal<UserInfo | null>(null);
  readonly selectedPermission = signal<UserPermission | null>(null);
  readonly selectedAcademicOfficer = signal<any>(null);

  // API-fetched user roles
  readonly apiUserRoles = signal<UserRole[]>([]);
  readonly rolesLoading = signal<boolean>(false);
  /**
   * True once GET /me/ has answered — with roles, with none, or with an error.
   *
   * Distinct from `!rolesLoading()`, which is ALSO true before the request has
   * started: the fetch is kicked off by the `userInfo$` subscription, so anyone
   * waiting on `rolesLoading` alone sails straight through in the window that
   * matters most — the moment right after an OAuth return. Restored roles from
   * localStorage count as resolved, so a reload does not stall on the network.
   */
  readonly rolesResolved = signal<boolean>(false);

  // Computed properties
  readonly currentGroups = computed(() => this.selectedPermission()?.groups || []);
  readonly isAcademicOfficer = computed(() => this.selectedPermission()?.isAcademicOfficer || false);
  readonly uni = computed(() => this.currentUser()?.info?.email?.split('@')[0] || '');

  // Categorized roles by tier
  readonly categorizedRoles = computed<CategorizedRoles>(() => {
    const roles = this.apiUserRoles();
    return {
      tier1: roles.filter(r => (ROLE_TIERS[r.group_name] ?? 99) === 1),
      tier2: roles.filter(r => (ROLE_TIERS[r.group_name] ?? 99) === 2),
      tier3: roles.filter(r => (ROLE_TIERS[r.group_name] ?? 99) === 3),
    };
  });

  // Compact single-line summary for header display
  readonly roleSummary = computed(() => {
    const cat = this.categorizedRoles();
    const parts: string[] = [];
    if (cat.tier1.length > 0) {
      parts.push(cat.tier1.map(r => GROUP_DISPLAY_NAMES[r.group_name] || r.group_name).join(', '));
    }
    if (cat.tier2.length > 0) {
      const label = GROUP_DISPLAY_NAMES[cat.tier2[0].group_name] || 'School';
      parts.push(label + ': ' + cat.tier2.map(r => r.reporting_unit?.name || 'Unknown RU').join(', '));
    }
    if (cat.tier3.length > 0) {
      const label = GROUP_DISPLAY_NAMES[cat.tier3[0].group_name] || 'Dept';
      parts.push(label + ': ' + cat.tier3.map(r => r.department?.description || r.department?.name || 'Unknown Dept').join(', '));
    }
    return parts.join(' · ');
  });
  
  // Impersonation display properties
  readonly displayName = computed(() => {
    const officer = this.selectedAcademicOfficer();
    if (officer && this.isAcademicOfficer()) {
      return `${officer.first_name} ${officer.last_name} (${officer.uni})`;
    }
    return this.currentUser()?.info?.name || '';
  });

  readonly displayPermissionLabel = computed(() => {
    const permission = this.selectedPermission();
    const officer = this.selectedAcademicOfficer();
    
    if (officer && permission?.isAcademicOfficer) {
      return `${permission.label} - Impersonating ${officer.first_name} ${officer.last_name}`;
    }
    return permission?.label || '';
  });

  readonly displayEmpId = computed(() => {
    const officer = this.selectedAcademicOfficer();
    return officer?.empId || '';
  });

  readonly displayEmail = computed(() => {
    const officer = this.selectedAcademicOfficer();
    return officer?.sub || '';
  });

  // Available permissions based on the feature file
  readonly availablePermissions: readonly UserPermission[] = [
    {
      key: 'admin',
      label: 'Academic Appointment Admin',
      groups: ['academic_appoint_admin'],
      isAcademicOfficer: false,
      isCuimc: false,
      tier: 1
    },
    {
      key: 'user',
      label: 'Academic Appointment User',
      groups: ['academic_appoint_user'],
      isAcademicOfficer: false,
      isCuimc: false,
      tier: 1
    },
    {
      // No distinct "CUIMC admin" group exists on the backend — academic_appoint_admin
      // is already all-access (Morningside + CUIMC), so there is nothing narrower to
      // select here. This permission intentionally has no CUIMC-only counterpart.
      key: 'user-cuimc',
      label: 'Academic Appointment User (CUIMC)',
      groups: ['academic_appoint_user_cuimc'],
      isAcademicOfficer: false,
      isCuimc: true,
      tier: 1
    },
    {
      // Reporting-unit scoped viewer (backend group school_institute_libraries_level)
      key: 'school-center-user',
      label: 'School / Center User (Reporting Unit)',
      groups: ['school_institute_libraries_level'],
      isAcademicOfficer: false,
      isCuimc: false,
      tier: 2,
      scopeType: 'reporting_unit',
      // Only Academic Appointments roles reach across reporting units.
      impersonatableBy: [...AA_GROUPS]
    },
    {
      // Department scoped viewer (backend group department_center_level)
      key: 'department-user',
      label: 'Department User',
      groups: ['department_center_level'],
      isAcademicOfficer: false,
      isCuimc: false,
      tier: 3,
      scopeType: 'department',
      // A reporting-unit admin may view as any department beneath their RU;
      // the backend narrows the pickable departments to their own scope.
      impersonatableBy: [...AA_GROUPS, RU_ADMIN_GROUP]
    },
    {
      key: 'academic-officer',
      label: 'Academic Officer',
      groups: ['academic-officer'],
      isAcademicOfficer: true,
      isCuimc: false,
      tier: 4,
      // Every impersonation-capable role can view as an officer; which officers
      // are offered is scoped server-side by the officer's admin department and
      // that department's reporting unit.
      impersonatableBy: [...IMPERSONATION_CAPABLE_GROUPS]
    },
    {
      key: 'academic-officer-cuimc',
      label: 'Academic Officer (CUIMC)',
      groups: ['academic-officer', 'cuimc'],
      isAcademicOfficer: true,
      isCuimc: true,
      tier: 4,
      impersonatableBy: [...IMPERSONATION_CAPABLE_GROUPS]
    },
    {
      // Reviews leave requests after the Academic Appointments stage and does
      // nothing else — no officer records, no PAC feed, no reports, no admin.
      key: 'vice-provost-faculty-affairs',
      label: 'Vice Provost for Faculty Affairs',
      groups: ['vice_provost_faculty_affairs'],
      isAcademicOfficer: false,
      isCuimc: false,
      tier: 1,
      impersonatableBy: [...AA_GROUPS]
    }
  ] as const;

  constructor() {
    // Load persisted state on initialization
    this.loadPersistedState();
    
    // Subscribe to auth service user info changes
    this.authService.userInfo$.subscribe(userInfo => {
      if (userInfo) {
        // Store the user info directly as it's already in the correct format
        this.currentUser.set(userInfo);
        this.isSignedIn.set(true);
        this.savePersistedState();

        // Fetch actual roles from API via GET /me/ (authenticated user's own profile)
        if (!this.rolesLoading()) {
          this.fetchUserRoles();
        }
      }
    });
    
    // Subscribe to auth changes
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (!isAuth) {
        this.resetPermission();
      }
    });
  }

  private loadPersistedState(): void {
    try {
      const isSignedIn = localStorage.getItem('caps_isSignedIn') === 'true';
      const userStr = localStorage.getItem('caps_currentUser');
      const permissionStr = localStorage.getItem('caps_selectedPermission');
      const officerStr = localStorage.getItem('caps_selectedAcademicOfficer');

      if (isSignedIn && userStr && permissionStr) {
        const user = JSON.parse(userStr);
        const permission = JSON.parse(permissionStr);

        this.isSignedIn.set(true);
        this.currentUser.set(user);
        this.selectedPermission.set(permission);

        if (officerStr) {
          const officer = JSON.parse(officerStr);
          this.selectedAcademicOfficer.set(officer);
        }

        // Restore the impersonation target so reloads keep the header in sync.
        const targetStr = localStorage.getItem('caps_impersonationTarget');
        if (targetStr) {
          this.impersonationContext.set(JSON.parse(targetStr) as ImpersonationTarget);
        }

        const rolesStr = localStorage.getItem('caps_apiUserRoles');
        if (rolesStr) {
          const restored = JSON.parse(rolesStr) as UserRole[];
          this.apiUserRoles.set(restored);
          // A reload already knows what this user may reach; make anyone
          // waiting on role resolution proceed immediately rather than sit
          // through another GET /me/ round trip.
          if (restored.length) this.rolesResolved.set(true);
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted auth state:', error);
      // Clear any corrupted data
      this.clearPersistedState();
    }
  }

  private savePersistedState(): void {
    try {
      localStorage.setItem('caps_isSignedIn', this.isSignedIn().toString());
      
      const user = this.currentUser();
      if (user) {
        localStorage.setItem('caps_currentUser', JSON.stringify(user));
      }
      
      const permission = this.selectedPermission();
      if (permission) {
        localStorage.setItem('caps_selectedPermission', JSON.stringify(permission));
      }
      
      const officer = this.selectedAcademicOfficer();
      if (officer) {
        localStorage.setItem('caps_selectedAcademicOfficer', JSON.stringify(officer));
      } else {
        // Cleared (e.g. stopped impersonating) — don't let a stale officer
        // get restored on the next reload.
        localStorage.removeItem('caps_selectedAcademicOfficer');
      }

      // Persist the impersonation target (officer / scope / group) so a reload
      // keeps sending the right X-Impersonate-* headers.
      const target = this.impersonationContext.target();
      if (target) {
        localStorage.setItem('caps_impersonationTarget', JSON.stringify(target));
      } else {
        localStorage.removeItem('caps_impersonationTarget');
      }

      const roles = this.apiUserRoles();
      if (roles.length > 0) {
        localStorage.setItem('caps_apiUserRoles', JSON.stringify(roles));
      }
    } catch (error) {
      console.warn('Failed to save persisted auth state:', error);
    }
  }

  private clearPersistedState(): void {
    localStorage.removeItem('caps_isSignedIn');
    localStorage.removeItem('caps_currentUser');
    localStorage.removeItem('caps_selectedPermission');
    localStorage.removeItem('caps_selectedAcademicOfficer');
    localStorage.removeItem('caps_impersonationTarget');
    localStorage.removeItem('caps_apiUserRoles');
  }

  private initializeMockUser(): void {
    // Mock user data from userinfo.openid endpoint
    const mockUser: UserInfo = {
      info: {
        sub: 'js5471@columbia.edu',
        name: 'Jone Sudhakar',
        given_name: 'Jone',
        family_name: 'Sudhakar',
        email: 'js5471@columbia.edu'
      }
    };
    
    this.currentUser.set(mockUser);
  }

  private fetchUserRoles(): void {
    this.rolesLoading.set(true);
    this.capsService.getCurrentUser().subscribe({
      next: (user) => {
        if (user && Array.isArray(user.roles)) {
          const mapped = user.roles
            .map((r: any) => mapApiRoleToUserRole(r))
            .filter((r: UserRole) =>
              r.group_name !== 'academic_officer_faculty' &&
              r.group_name !== 'academic_officer_non_faculty'
            )
            .sort(compareRolesWidestFirst);
          this.apiUserRoles.set(mapped);
          if (mapped.length === 0) {
            // Account exists but has no usable roles assigned — show the
            // same "Account Not Provisioned" full-page screen as 401-from-/me/.
            this.appErrorService.setNotProvisionedError();
          } else if (!this.impersonationContext.isImpersonating()) {
            // Set the session permission from the user's OWN widest role.
            // Authoritative here (roles are now loaded) — this also clears any
            // stale default from a previous session.
            const own = this.defaultPermissionForRoles();
            if (own) this.selectedPermission.set(own);
          }
          this.savePersistedState();
        } else {
          this.apiUserRoles.set([]);
          this.appErrorService.setNotProvisionedError();
        }
        this.rolesLoading.set(false);
        this.rolesResolved.set(true);
      },
      error: (err) => {
        console.error('Failed to fetch current user roles:', err);
        this.rolesLoading.set(false);
        // Resolved, in the sense that waiting any longer will not help. Anyone
        // blocked on this proceeds with whatever roles are known.
        this.rolesResolved.set(true);
      }
    });
  }

  signIn(): void {
    this.initializeMockUser();
    this.isSignedIn.set(true);
    this.savePersistedState();
  }

  resetPermission(): void {
    this.isSignedIn.set(false);
    this.currentUser.set(null);
    this.selectedPermission.set(null);
    this.selectedAcademicOfficer.set(null);
    this.impersonationContext.clear();
    this.apiUserRoles.set([]);
    this.rolesResolved.set(false);
    this.clearPersistedState();
    this.router.navigate(['']);
  }

  // Clear permission state without redirecting (used for token expiry)
  clearStateWithoutRedirect(): void {
    this.isSignedIn.set(false);
    this.currentUser.set(null);
    this.selectedPermission.set(null);
    this.selectedAcademicOfficer.set(null);
    this.impersonationContext.clear();
    this.apiUserRoles.set([]);
    this.rolesResolved.set(false);
    this.clearPersistedState();
  }

  changePermission(): void {
    // Navigate to the impersonation ("View As") screen without a full logout.
    this.router.navigate(['/impersonate']);
  }

  signOut(): void {
    console.log('🟡 PermissionService.signOut() called');

    // Clear application permissions and user state
    this.resetPermission();

    // Clear all auth storage, terminate SSO session in background, show login page
    this.authService.logout();
  }

  selectPermission(permission: UserPermission): void {
    this.selectedPermission.set(permission);
    
    // Clear academic officer impersonation if switching to non-academic officer permission
    if (!permission.isAcademicOfficer) {
      this.selectedAcademicOfficer.set(null);
      this.impersonationContext.clear();
    }

    this.savePersistedState();
  }

  async selectAcademicOfficer(officer: any): Promise<void> {
    this.selectedAcademicOfficer.set(officer);
    // Enter read-only "view as" mode: subsequent API calls carry the
    // X-Impersonate-Officer header (see JsonaService.getHeaders).
    if (officer?.emp_id) {
      this.impersonationContext.set({
        kind: 'officer',
        empId: String(officer.emp_id),
        label: `${officer.first_name} ${officer.last_name} (${officer.uni})`,
      });
    } else {
      this.impersonationContext.clear();
    }
    this.savePersistedState();
    
    // Auto-navigate to academic officer dashboard
    await this.router.navigate(['/academic-officer', officer.emp_id]);
  }

  /**
   * Where this user belongs when no particular page was asked for — the single
   * answer to "home", used by the post-sign-in landing, the route guard's
   * fallbacks and the Dashboard button alike.
   *
   * An Academic Officer session is NOT at home on /dashboard: that route's
   * `requiredGroups` do not include 'academic-officer', so sending one there
   * lands them on /unauthorized. Their home is their own officer page.
   *
   * An officer permission with no officer loaded cannot survive role
   * resolution, but must never produce a broken URL in the meantime — so it
   * falls back to /dashboard rather than /academic-officer/undefined.
   */
  homeRoute(): string {
    const permission = this.selectedPermission();
    const empId = this.selectedAcademicOfficer()?.emp_id;
    if (permission?.isAcademicOfficer && empId) {
      return `/academic-officer/${empId}`;
    }
    return '/dashboard';
  }

  /**
   * Resolves once GET /me/ has answered, or after `timeoutMs` — whichever
   * comes first. The timeout is what stops a hung /me/ from leaving the user
   * on a blank screen: they get their home page instead of nothing.
   */
  waitForRoles(timeoutMs = PermissionService.ROLE_RESOLUTION_TIMEOUT_MS): Promise<void> {
    if (this.rolesResolved()) return Promise.resolve();

    return new Promise<void>(resolve => {
      let done = false;
      let ref: EffectRef | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish = () => {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        // Deferred: the effect can settle on its own first run, which happens
        // before this assignment returns.
        queueMicrotask(() => ref?.destroy());
        resolve();
      };
      timer = setTimeout(finish, timeoutMs);
      ref = effect(() => {
        if (this.rolesResolved()) finish();
      }, { injector: this.injector });
    });
  }

  async redirectToDashboard(): Promise<void> {
    if (!this.selectedPermission()) return;
    await this.router.navigateByUrl(this.homeRoute());
  }

  /**
   * The widest privilege tier the current user actually holds (from GET /me/
   * roles). 1 = widest. Impersonation targets wider than this are disabled.
   * Returns Infinity when the user has no known roles (nothing impersonable).
   */
  readonly userBestTier = computed<number>(() => {
    const roles = this.apiUserRoles();
    if (!roles.length) return Infinity;
    return Math.min(...roles.map(r => ROLE_TIERS[r.group_name] ?? 99));
  });

  /** A target is impersonable only if it is equal or narrower than the user's own scope. */
  /**
   * Impersonation authority, per the role matrix:
   *   AAA / AAU            → reporting-unit roles, department roles, officers, VPFA
   *   Reporting-unit admin → department roles and officers (within their RU)
   *   Department admin     → officers only (within their department)
   * Anyone may still select a role they actually hold — that is switching which
   * of your own roles you act as, not impersonation.
   */
  isPermissionAvailable(permission: UserPermission): boolean {
    const own = new Set(this.apiUserRoles().map(r => r.group_name));
    if (permission.groups.some(g => own.has(g))) return true;
    return (permission.impersonatableBy ?? []).some(g => own.has(g));
  }

  /** True when the user holds a role that may impersonate anyone at all. */
  canImpersonate(): boolean {
    const own = new Set(this.apiUserRoles().map(r => r.group_name));
    return IMPERSONATION_CAPABLE_GROUPS.some(g => own.has(g));
  }

  /** Backend role group_name → the `availablePermissions` key that represents it. */
  private static readonly ROLE_TO_PERMISSION_KEY: Record<string, string> = {
    academic_appoint_admin: 'admin',
    academic_appoint_user: 'user',
    academic_appoint_user_cuimc: 'user-cuimc',
    school_institute_libraries_level: 'school-center-user',
    department_center_level: 'department-user',
    // The VP for Faculty Affairs has its own narrow permission — mapping it to
    // 'user' previously gave it the full Academic Appointment User navbar.
    vice_provost_faculty_affairs: 'vice-provost-faculty-affairs',
    // Remaining all-access read-only roles still behave like an AA User here.
    faculty_affairs: 'user',
    provost_office: 'user',
    cfo: 'user',
  };

  /**
   * The permission that reflects the user's OWN widest role (from GET /me/).
   * Used as the default session permission and to restore the navbar after
   * impersonation ends. Returns null when no role maps to a known permission.
   */
  defaultPermissionForRoles(): UserPermission | null {
    const roles = this.apiUserRoles();
    if (!roles.length) return null;
    const widestFirst = [...roles].sort(compareRolesWidestFirst);
    for (const role of widestFirst) {
      const key = PermissionService.ROLE_TO_PERMISSION_KEY[role.group_name];
      const perm = key ? this.availablePermissions.find(p => p.key === key) : undefined;
      if (perm) return perm;
    }
    return null;
  }

  /**
   * Resolve which leave-request review queue an active *impersonation*
   * preview should reflect (admin previewing what a narrower role would
   * see). Returns null when not impersonating, when impersonating a
   * specific officer (no review queue), or when there's no scoped context —
   * in all of those cases the caller should use
   * `JsonaService.getLeaveReviewBreakdown()` instead, which sums across
   * every review role the caller actually holds server-side.
   */
  resolveLeaveReviewContext(): { level: string; reportingUnitId?: string; departmentId?: string } | null {
    const target = this.impersonationContext.target();
    if (!target) return null;
    if (target.kind === 'scope' && target.scopeType === 'reporting_unit') {
      return { level: 'school', reportingUnitId: target.id };
    }
    if (target.kind === 'scope' && target.scopeType === 'department') {
      return { level: 'department', departmentId: target.id };
    }
    if (target.kind === 'group') {
      return { level: 'academic_appointments' };
    }
    return null; // impersonating a specific officer — no review queue
  }

  /** Explanation shown when a target is disabled. */
  unavailableReason(permission: UserPermission): string {
    return `Unavailable — your assigned roles cannot view as ${permission.label}.`;
  }

  /**
   * Enter read-only impersonation for a validated target. Keeps the admin's
   * real identity; sets the header context so subsequent API calls are scoped.
   */
  async enterImpersonation(
    permission: UserPermission,
    target: ImpersonationTarget,
    officer?: any,
  ): Promise<void> {
    this.selectedPermission.set(permission);
    this.selectedAcademicOfficer.set(target.kind === 'officer' ? officer ?? null : null);
    this.impersonationContext.set(target);
    this.savePersistedState();

    await this.router.navigateByUrl(this.homeRoute());
  }

  /**
   * Exit read-only impersonation and return to the admin's own dashboard.
   * Clears the selected officer + the header context but keeps the admin
   * signed in with their real permission.
   */
  async stopImpersonating(): Promise<void> {
    this.selectedAcademicOfficer.set(null);
    this.impersonationContext.clear();
    // Restore the navbar/permission to the user's OWN widest role (the
    // impersonated permission must not linger after stopping).
    const own = this.defaultPermissionForRoles();
    if (own) this.selectedPermission.set(own);
    this.savePersistedState();
    await this.router.navigateByUrl(this.homeRoute());
  }

  /** True while viewing the app as an Academic Officer (read-only). */
  readonly isImpersonating = computed(() => this.impersonationContext.isImpersonating());

  /** Officer emp_id being viewed as, or null when not impersonating an officer. */
  readonly impersonatedEmpId = computed(() => this.impersonationContext.impersonatedEmpId());

  /**
   * Whether this user may draft and submit a leave request for the officer they
   * are viewing as.
   *
   * Temporary carve-out from read-only impersonation: an Academic Appointments
   * admin viewing as an Academic Officer otherwise cannot do the one thing that
   * role exists for.
   *
   * This decides only whether the controls are OFFERED. The backend enforces the
   * same rule independently from its own list, so a stale UI build cannot grant
   * anything — and it additionally checks the request is for the impersonated
   * officer rather than merely someone in scope.
   *
   * Officer impersonation only: viewing as a reporting unit or department role
   * stays entirely read-only.
   */
  readonly canSelfSubmitWhileImpersonating = computed(() => {
    if (!this.impersonatedEmpId()) return false;
    return this.isListedIn(ENV.impersonationSelfSubmitUsers);
  });

  /**
   * Testing bypass: hide the evidence and letter prerequisites on a leave
   * request, so it can be walked through the workflow without real documents.
   *
   * Decides only what the UI offers. The backend enforces the same rule from
   * LEAVE_BYPASS_ATTACHMENT_GATES_USERS, so this cannot grant anything on its
   * own — at worst it shows an enabled button the server then refuses.
   */
  readonly canBypassAttachmentGates = computed(() =>
    this.isListedIn(ENV.bypassAttachmentGatesUsers));

  /**
   * Testing bypass: show every review stage regardless of the roles held, so
   * one account can act at all of them.
   *
   * Mirrors LEAVE_BYPASS_REVIEW_ROLES_USERS on the backend, which also lifts the
   * department / reporting-unit scope check. Stage order is still enforced
   * there — a request is reviewed at the stage it has reached.
   */
  readonly canBypassReviewRoles = computed(() =>
    this.isListedIn(ENV.bypassReviewRolesUsers));

  /** Whether the signed-in user's email appears in a feature-flag allowlist. */
  private isListedIn(allowlist: readonly string[] | undefined): boolean {
    const email = (this.currentUser()?.info?.email ?? '').trim().toLowerCase();
    if (!email) return false;
    return (allowlist ?? []).some(allowed => allowed.trim().toLowerCase() === email);
  }

  hasGroup(group: string): boolean {
    return this.currentGroups().includes(group);
  }

  hasAnyGroup(groups: string[]): boolean {
    const currentGroups = this.currentGroups();
    return groups.some(group => currentGroups.includes(group));
  }

  /**
   * Every group the user effectively holds, for feature gating.
   *
   * Differs from `currentGroups()`, which is only the single permission the user
   * is "acting as" (the widest role, via `defaultPermissionForRoles`). A user
   * assigned both academic_appoint_admin and department_center_level acts as the
   * admin, so `currentGroups()` would NOT contain department_center_level —
   * hiding department-level features from someone who legitimately holds the role.
   *
   * While impersonating, this narrows to the impersonated permission's groups so
   * the preview shows exactly what that single role can see.
   */
  readonly effectiveGroups = computed<readonly string[]>(() => {
    if (this.isImpersonating()) return this.selectedPermission()?.groups ?? [];
    const assigned = this.apiUserRoles().map(r => r.group_name);
    // Fall back to the selected permission while GET /me/ is still in flight.
    return assigned.length ? assigned : (this.selectedPermission()?.groups ?? []);
  });

  /** True if the user is assigned this role (see `effectiveGroups`). */
  hasAssignedRole(group: string): boolean {
    return this.effectiveGroups().includes(group);
  }

  /** True if the user is assigned any of these roles (see `effectiveGroups`). */
  hasAnyAssignedRole(groups: string[]): boolean {
    const effective = this.effectiveGroups();
    return groups.some(group => effective.includes(group));
  }

  isAdmin(): boolean {
    return this.hasGroup('academic_appoint_admin');
  }

  canAccessRoute(requiredGroups: string[]): boolean {
    if (requiredGroups.length === 0) return true;
    return this.hasAnyGroup(requiredGroups);
  }

  getDefaultAcademicOfficers() {
    return this.capsService.searchAcademicOfficer('');
  }

  searchAcademicOfficers(query: string) {
    return this.capsService.searchAcademicOfficer(query);
  }

  getAcademicOfficer(empId: string) {
    return this.capsService.getAcademicOfficer(empId);
  }
}